import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { RecipeV2Detail, AllergenStatus, RecipeAllergenSummary } from '../types';
import { UserContext } from '../../../components/shared/useUser';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import AllergenChips from '../../../components/shared/AllergenChips';

interface AllergensTabProps {
  recipe: RecipeV2Detail;
}

const statusColor: Record<AllergenStatus, 'error' | 'warning' | 'success'> = {
  [AllergenStatus.Contains]: 'error',
  [AllergenStatus.MayContain]: 'warning',
  [AllergenStatus.FreeFrom]: 'success',
};

const AllergensTab = ({ recipe }: AllergensTabProps) => {
  const { t } = useTranslation('recipeV2');
  const { hasPermissionTo } = useContext(UserContext);
  const api = useRecipeV2Api();
  const canOverride = hasPermissionTo(['RecipeV2AllergenOverride']);

  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedAllergen, setSelectedAllergen] = useState<RecipeAllergenSummary | null>(null);
  const [overrideStatus, setOverrideStatus] = useState<AllergenStatus>(AllergenStatus.FreeFrom);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);

  const handleOpenOverride = (allergen: RecipeAllergenSummary) => {
    setSelectedAllergen(allergen);
    setOverrideStatus(allergen.status === AllergenStatus.Contains ? AllergenStatus.FreeFrom : AllergenStatus.Contains);
    setOverrideReason('');
    setOverrideDialogOpen(true);
  };

  const handleApplyOverride = async () => {
    if (!selectedAllergen) return;
    setOverrideSaving(true);
    try {
      await api.overrideAllergen(recipe.id, {
        allergenId: selectedAllergen.allergenId,
        overrideStatus,
        reason: overrideReason,
      });
      setOverrideDialogOpen(false);
    } catch { /* noop */ }
    setOverrideSaving(false);
  };

  if (recipe.allergens.length === 0) {
    return (
      <Paper variant="outlined"
        sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('allergens.noAllergens')}</Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Summary chips */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>{t('allergens.title')}</Typography>
        <Stack direction="row"
          spacing={1}
          flexWrap="wrap">
          <AllergenChips
            allergens={recipe.allergens}
            showName
            size="medium"
          />
        </Stack>
      </Paper>

      {/* Traceability table */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>{t('allergens.traceability')}</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('allergens.allergen')}</TableCell>
                <TableCell>{t('statusLabel')}</TableCell>
                <TableCell>{t('source')}</TableCell>
                <TableCell>{t('allergens.introducedBy')}</TableCell>
                <TableCell>{t('allergens.path')}</TableCell>
                {canOverride && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {recipe.allergens.map((allergen) =>
                allergen.introducedBy.length > 0 ? (
                  allergen.introducedBy.map((trace, idx) => (
                    <TableRow key={`${allergen.allergenCode}-${idx}`}>
                      {idx === 0 ? (
                        <>
                          <TableCell rowSpan={allergen.introducedBy.length}>
                            <Chip
                              label={allergen.allergenName}
                              size="small"
                              color={statusColor[allergen.status] ?? 'default'}
                            />
                          </TableCell>
                          <TableCell rowSpan={allergen.introducedBy.length}>
                            {t(`allergens.${allergen.status.charAt(0).toLowerCase() + allergen.status.slice(1)}`)}
                          </TableCell>
                          <TableCell rowSpan={allergen.introducedBy.length}>
                            {allergen.source}
                          </TableCell>
                        </>
                      ) : null}
                      <TableCell>{trace.componentDescription}</TableCell>
                      <TableCell>
                        <Typography variant="textXS"
                          color="text.secondary">
                          {trace.path}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow key={allergen.allergenCode}>
                    <TableCell>
                      <Chip label={allergen.allergenName}
                        size="small"
                        color={statusColor[allergen.status] ?? 'default'} />
                    </TableCell>
                    <TableCell>{t(`allergens.${allergen.status.charAt(0).toLowerCase() + allergen.status.slice(1)}`)}</TableCell>
                    <TableCell>{allergen.source}</TableCell>
                    <TableCell colSpan={2}>
                      <Typography variant="textSM"
                        color="text.secondary">—</Typography>
                    </TableCell>
                    {canOverride && (
                      <TableCell>
                        <Button size="md"
                          startIcon={<Edit />}
                          onClick={() => handleOpenOverride(allergen)}>
                          {t('allergens.override')}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Override dialog */}
      <Dialog open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('allergens.overrideTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ mt: 1 }}>
            {selectedAllergen && (
              <Chip label={selectedAllergen.allergenName}
                size="small"
                color={statusColor[selectedAllergen.status] ?? 'default'} />
            )}
            <Select
              value={overrideStatus}
              onChange={(e) => setOverrideStatus(e.target.value as AllergenStatus)}
              size="small"
              fullWidth
            >
              <MenuItem value={AllergenStatus.Contains}>{t('allergens.contains')}</MenuItem>
              <MenuItem value={AllergenStatus.MayContain}>{t('allergens.mayContain')}</MenuItem>
              <MenuItem value={AllergenStatus.FreeFrom}>{t('allergens.freeFrom')}</MenuItem>
            </Select>
            <TextField
              multiline
              rows={3}
              fullWidth
              label={t('allergens.overrideReason')}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}
            variant="secondary">
            {t('allergens.overrideCancel')}
          </Button>
          <Button
            onClick={handleApplyOverride}
            variant="primary"
            disabled={overrideSaving || overrideReason.trim() === ''}
          >
            {t('allergens.overrideConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AllergensTab;
